import { RpcError } from '@protobuf-ts/runtime-rpc'
import { SimpleConfirmationDialog } from 'components/core/confirmation/dialog/SimpleConfirmationDialog'
import { C1Link } from 'components/core/links/C1Link'
import { ClosableProps } from 'components/no-ui/context/modal-provider'
import { useAlert } from 'hooks/useAlert/index'
import { TaskValue } from 'lib/actions/task'
import { routes } from 'lib/routes'
import { FC, useCallback } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useTicketActionTracker } from 'redux_toolkit/lib/TicketActionTracker'

export type EscalateToEmergencyAccessDialogV2Props = {
    task: TaskValue
} & ClosableProps

export const EscalateToEmergencyAccessDialogV2: FC<EscalateToEmergencyAccessDialogV2Props> = ({ task, close }) => {
    const intl = useIntl()
    const toast = useAlert()
    const ticketActionTracker = useTicketActionTracker()

    const handleClick = useCallback(async (): Promise<void> => {
        try {
            await ticketActionTracker?.escalate(task)
        } catch (err: unknown) {
            return toast(
                <FormattedMessage
                    id="task_escalate_to_emergency_access_error"
                    defaultMessage="Error escalating task {task_link}. {code}: {msg}"
                    values={{
                        task_link: <C1Link href={routes.task(String(task.numericId || ''))}>{task.numericId}</C1Link>,
                        code: (err as RpcError).code,
                        msg: (err as RpcError).message,
                    }}
                />,
                'error',
            )
        }

        toast(
            <FormattedMessage
                id="task_escalate_to_emergency_access_success"
                defaultMessage="Task {task_link} escalated successfully"
                values={{
                    task_link: <C1Link href={routes.task(String(task.numericId || ''))}>{task.numericId}</C1Link>,
                }}
            />,
            'success',
        )

        close()
    }, [close, task, ticketActionTracker, toast])

    return (
        <SimpleConfirmationDialog
            submitActionLabel={intl.formatMessage({
                id: 'escalate_confirmation_dialog_action_string',
                defaultMessage: 'Escalate',
            })}
            title={intl.formatMessage({
                id: 'escalate_confirmation_dialog_title',
                defaultMessage: 'Are you sure you want to escalate this request?',
            })}
            details={intl.formatMessage({
                id: 'escalate_confirmation_dialog_description',
                defaultMessage:
                    'Escalating this request swaps in the emergency access policy for its current policy. Any reviews this request has received so far will be reset.',
            })}
            onConfirm={handleClick}
            onCancel={close}
            inProgress={!ticketActionTracker}
            close={close}
        />
    )
}
